import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

// Non-sensitive cart draft — only dish_id/name/price/qty/cook_name. No auth tokens,
// no payment info, no PII. Persisted in localStorage on purpose so buyers don't lose
// their pre-order draft on page refresh. Real order lock happens server-side at /orders.
const KEY = "cookingin_cart_draft_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY));
      if (!Array.isArray(raw)) return [];
      // Whitelist fields — never trust stored JSON to introduce extra keys.
      return raw.map((i) => ({
        dish_id: String(i.dish_id || ""),
        name: String(i.name || ""),
        price: Number(i.price) || 0,
        qty: Math.max(0, Number(i.qty) || 0),
        cook_name: String(i.cook_name || ""),
      })).filter((i) => i.dish_id && i.qty > 0);
    } catch (err) {
      console.error("Failed to restore cart draft:", err);
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); }
    catch (err) { console.error("Failed to persist cart draft:", err); }
  }, [items]);

  const add = (dish) =>
    setItems((prev) => {
      const found = prev.find((i) => i.dish_id === dish.dish_id);
      if (found) return prev.map((i) => (i.dish_id === dish.dish_id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { dish_id: dish.dish_id, name: dish.name, price: dish.price, qty: 1, cook_name: dish.cook_name }];
    });

  const setQty = (dish_id, qty) =>
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.dish_id !== dish_id) : prev.map((i) => (i.dish_id === dish_id ? { ...i, qty } : i))));

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);

  const value = useMemo(
    () => ({ items, add, setQty, clear, count, subtotal }),
    [items, count, subtotal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
